import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import ConversationStatusChips from "@/components/ConversationStatusChips";
import { scoreToRating, scoreToTone } from "@/lib/scoreGrade";
import {
  ArrowRight,
  CaretDown,
  CheckCircle,
  ClockCounterClockwise,
  Flag,
  TrendUp,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeRepText(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/\b(?:sales rep|sales representative|account manager|salesperson|sales agent|commercial)\b/gi, "rep")
    .replace(/^(?:(?:the|il|le|el|der)\s+)?rep\s*:\s*/i, "")
    .replace(/^(?:(?:the|il|le|el|der)\s+)?rep\s+(?:must|should|will|needs to|committed to|deve|dovrà|dovrebbe|si è impegnato a|doit|devra|s'est engagé à|debe|deberá|se comprometió a|muss|soll|wird)\s+/i, "")
    .trim();
}

function capitalizeFirstLetter(value) {
  if (typeof value !== "string") return value;
  for (let index = 0; index < value.length; index += 1) {
    if (/\p{L}/u.test(value[index])) {
      return value.slice(0, index) + value[index].toLocaleUpperCase() + value.slice(index + 1);
    }
  }
  return value;
}

function itemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return normalizeRepText(item);
  if (typeof item !== "object") return String(item);
  return normalizeRepText(item.what || item.text || item.answer || item.description || "");
}

function narrativeLines(value) {
  const normalized = normalizeRepText(String(value || "")).trim();
  if (!normalized) return [];

  return normalized
    .split(/\s*[·•]\s*|\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function evolutionStep(raw) {
  if (raw == null) return { text: "", status: "" };
  if (typeof raw === "string") return { text: raw, status: "" };
  return {
    text: raw.text || raw.objection || raw.what || "",
    status: String(raw.status || raw.state || "").toLowerCase(),
  };
}

function objectionStatusFor(text, evolution = []) {
  const normalize = (value) => String(value || "")
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  const target = normalize(text);
  if (!target) return "";

  for (const rawStep of evolution || []) {
    const step = evolutionStep(rawStep);
    const candidate = normalize(step.text);
    if (!candidate) continue;
    if (candidate === target || candidate.includes(target) || target.includes(candidate)) {
      return step.status;
    }
  }
  return "";
}

function objectionTone(status, text, fallbackTone = "caution") {
  if (!text || text === "—") return "good";
  const normalized = String(status || "").toLowerCase();
  if (normalized === "resolved") return "good";
  if (normalized === "improving") return "bridge";
  if (normalized === "persistent") return "warning";
  if (normalized === "critical") return "danger";
  return fallbackTone || "caution";
}

function PreviewMetricValue({ value, className = "" }) {
  const valueRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const text = String(value || "—");

  const measure = useCallback(() => {
    const node = valueRef.current;
    if (!node) return;
    setTruncated(
      node.scrollHeight > node.clientHeight + 1
      || node.scrollWidth > node.clientWidth + 1
    );
  }, []);

  useLayoutEffect(() => {
    const node = valueRef.current;
    if (!node) return undefined;
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, text]);

  useEffect(() => {
    if (!tooltip) return undefined;
    const hide = () => setTooltip(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [tooltip]);

  const showTooltip = () => {
    if (!truncated || window.matchMedia("(max-width: 700px)").matches) return;
    const rect = valueRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxWidth = Math.min(380, Math.max(240, window.innerWidth - 24));
    const left = Math.min(Math.max(12, rect.left), Math.max(12, window.innerWidth - maxWidth - 12));
    const placeAbove = rect.bottom + 160 > window.innerHeight && rect.top > 180;
    setTooltip({
      left,
      top: placeAbove ? rect.top - 8 : rect.bottom + 8,
      above: placeAbove,
      maxWidth,
    });
  };

  return (
    <>
      <span
        ref={valueRef}
        className={`relationship-story-metric__value${className}`}
        tabIndex={truncated ? 0 : undefined}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setTooltip(null)}
        onFocus={showTooltip}
        onBlur={() => setTooltip(null)}
      >
        {text}
      </span>
      {tooltip && createPortal(
        <span
          className={`relationship-story-metric__tooltip${tooltip.above ? " is-above" : ""}`}
          role="tooltip"
          style={{
            left: `${tooltip.left}px`,
            top: `${tooltip.top}px`,
            maxWidth: `${tooltip.maxWidth}px`,
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </>
  );
}

function StoryMetric({ label, value, bar, tone = "", icon: Icon, multiline = false, preview = false }) {
  const valueClass = tone && bar !== undefined && bar !== null
    ? ` score-grade score-text score-text--${tone}`
    : "";
  return (
    <div className={`relationship-story-metric${Icon ? " has-icon" : ""}${multiline ? " is-multiline" : ""}${preview ? " is-preview" : ""}`}>
      {Icon && (
        <span className={`relationship-story-metric__icon${tone ? ` is-${tone}` : " is-violet"}`} aria-hidden="true">
          <Icon size={preview ? 15 : 18} weight="fill" />
        </span>
      )}
      <span className="relationship-story-metric__copy">
        <span className="relationship-story-metric__label">{label}</span>
        {preview && multiline ? (
          <PreviewMetricValue value={value} className={valueClass} />
        ) : (
          <span className={`relationship-story-metric__value${valueClass}`}>{value || "—"}</span>
        )}
        {bar !== undefined && bar !== null && (
          <span className="relationship-story-metric__track" aria-hidden="true">
            <span className={`relationship-story-metric__fill is-score-${tone}`} style={{ width: `${Math.max(0, Math.min(100, Number(bar) || 0))}%` }} />
          </span>
        )}
      </span>
    </div>
  );
}

function FlowNode({ phase, label, text, tone, icon: Icon, prominent = false, connected = false }) {
  return (
    <div className={`relationship-story-flow__node is-${tone}${prominent ? " is-prominent" : ""}`}>
      <div className="relationship-story-flow__phase">{phase}</div>
      <div className="relationship-story-flow__body">
        {connected && <FlowConnector />}
        <div className="relationship-story-flow__card">
          <span className={`relationship-story-flow__icon is-${tone}`} aria-hidden="true">
            <Icon size={19} weight={tone === "future" ? "fill" : "bold"} />
          </span>
          <span className="relationship-story-flow__copy">
            <small>{label}</small>
            <span className="relationship-story-flow__text">
              {narrativeLines(text).map((line, lineIndex) => (
                <span
                  className={`relationship-story-flow__text-line${lineIndex > 0 ? " is-point" : ""}`}
                  key={`${lineIndex}-${line}`}
                >
                  {lineIndex > 0 && (
                    <span className="relationship-story-flow__text-bullet" aria-hidden="true">·</span>
                  )}
                  <span>{line}</span>
                </span>
              ))}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

function FlowConnector() {
  return (
    <span className="relationship-story-flow__connector" aria-hidden="true">
      <span />
      <ArrowRight size={16} weight="bold" />
    </span>
  );
}

export default function ConversationHistoryTimeline({
  milestones = [],
  objectionEvolution = [],
  relationshipCallCount = milestones.length,
  activeCallId = "",
  getCallLink,
  allCollapsed = false,
  clickablePreviews = false,
  renderFollowUpAction,
  showCurrentBadge = true,
  className = "",
}) {
  const ordered = useMemo(() => [...milestones], [milestones]);
  const defaultExpanded = String(activeCallId || ordered[0]?.call_id || "");
  const [expandedId, setExpandedId] = useState(allCollapsed ? "" : defaultExpanded);
  const [outcomeTrackWidth, setOutcomeTrackWidth] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    setExpandedId(allCollapsed ? "" : defaultExpanded);
  }, [allCollapsed, defaultExpanded]);

  useLayoutEffect(() => {
    const root = listRef.current;
    if (!root) return undefined;

    let animationFrame = 0;
    let disposed = false;

    const measureOutcomeTrack = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        if (disposed) return;

        const outcomeChips = Array.from(
          root.querySelectorAll(
            ".relationship-story-card__chips .conversation-status-chip--outcome"
          )
        );
        const measuredWidth = Math.ceil(
          outcomeChips.reduce(
            (maximum, chip) => Math.max(maximum, chip.getBoundingClientRect().width),
            0
          )
        );

        setOutcomeTrackWidth((currentWidth) =>
          Math.abs(currentWidth - measuredWidth) < 1 ? currentWidth : measuredWidth
        );
      });
    };

    measureOutcomeTrack();

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measureOutcomeTrack);

    resizeObserver?.observe(root);
    root
      .querySelectorAll(".relationship-story-card__chips .conversation-status-chip--outcome")
      .forEach((chip) => resizeObserver?.observe(chip));

    window.addEventListener("resize", measureOutcomeTrack);
    document.fonts?.ready?.then(() => {
      if (!disposed) measureOutcomeTrack();
    });

    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", measureOutcomeTrack);
      resizeObserver?.disconnect();
    };
  }, [ordered, allCollapsed]);

  const sharedOutcomeTrackStyle = outcomeTrackWidth > 0
    ? { "--conversation-outcome-track-width": `${outcomeTrackWidth}px` }
    : undefined;

  return (
    <div
      ref={listRef}
      style={sharedOutcomeTrackStyle}
      className={`relationship-story-list${outcomeTrackWidth > 0 ? " has-shared-outcome-track" : ""}${className ? ` ${className}` : ""}`}
    >
      {ordered.map((milestone, index) => {
        const {
          call_id,
          title,
          date,
          customer_mood,
          main_objection,
          turning_point,
          next_step,
          negotiation_stage,
          confidence,
          churn_risk,
          outcome,
          relationship_update,
          objection_status,
        } = milestone;

        const isExpanded = String(call_id) === String(expandedId);
        const showDetails = !allCollapsed || isExpanded;
        const isActive = String(call_id) === String(activeCallId);
        const changeSummary = relationship_update?.changes?.summary || [];
        const pastText = changeSummary.map(itemText).filter(Boolean).join(" · ");
        const pastLabel = relationship_update?.previous_call_id ? "Since previous call" : "Initial relationship state";
        const matchedStatus = objection_status || objectionStatusFor(main_objection, objectionEvolution);
        const resolvedStatus = relationshipCallCount >= 2 || matchedStatus === "critical" ? matchedStatus : "";
        const intentTone = scoreToTone(confidence);
        const riskTone = scoreToTone(churn_risk, { invert: true });
        const objectionRiskTone = objectionTone(resolvedStatus, main_objection, riskTone);
        const nodes = [
          pastText && { phase: "Past", label: pastLabel, text: pastText, tone: "past", icon: ClockCounterClockwise },
          turning_point && { phase: "Present", label: "Turning point", text: normalizeRepText(turning_point), tone: "present", icon: TrendUp, prominent: true },
          next_step && { phase: "Future", label: "Next step", text: capitalizeFirstLetter(normalizeRepText(next_step)), tone: "future", icon: CheckCircle },
        ].filter(Boolean);
        const linkProps = typeof getCallLink === "function" ? getCallLink(milestone) : null;
        const previewIsClickable = Boolean(
          clickablePreviews && !isExpanded && linkProps?.to && !linkProps?.disabled
        );

        return (
          <div
            key={call_id || `${date}-${index}`}
            id={`timeline-call-${call_id}`}
            className={`relationship-timeline-item relationship-story-item${isActive ? " is-active" : ""}`}
          >
            <div className={`relationship-timeline-rail${index === ordered.length - 1 ? " is-last" : ""}`} aria-hidden="true"><span /></div>

            <article
              className={`relationship-timeline-card relationship-story-card${isExpanded ? " is-expanded" : " is-preview"}${!showDetails ? " is-header-only" : ""}${previewIsClickable ? " is-clickable" : ""}`}
            >
              {previewIsClickable && (
                <Link
                  to={linkProps.to}
                  state={linkProps.state}
                  className="relationship-story-card__preview-link"
                  aria-label={`Open ${title || "call"}`}
                  title={title || "Untitled call"}
                />
              )}
              <header className="relationship-story-card__header">
                <div className="relationship-story-card__identity">
                  <small>{fmtDate(date)}</small>
                  <strong title={title || "Untitled call"}>{title || "Untitled call"}</strong>
                </div>
                <ConversationStatusChips
                  outcome={outcome}
                  stage={negotiation_stage}
                  className="relationship-timeline-card__chips relationship-story-card__chips"
                  naturalBookmark
                />

                <div className="relationship-story-card__controls">
                  {typeof renderFollowUpAction === "function" ? renderFollowUpAction(milestone) : null}
                  {showCurrentBadge && isActive && (
                    <span className="relationship-story-card__current" aria-label="Current call">
                      Current
                    </span>
                  )}
                  <button
                    type="button"
                    className="relationship-story-card__toggle"
                    onClick={() => setExpandedId((current) => String(current) === String(call_id) ? "" : String(call_id))}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${title || "call"}`}
                  >
                    <CaretDown size={18} weight="bold" />
                  </button>
                </div>
              </header>

              {showDetails && (
                <div
                  className={`relationship-story-metrics${isExpanded ? "" : " is-preview"}`}
                  aria-label={isExpanded ? "Conversation signals" : "Conversation signals preview"}
                >
                  <StoryMetric label="Tone" value={customer_mood} preview={!isExpanded} multiline />
                  <StoryMetric label="Buying intent" value={scoreToRating(confidence)} bar={confidence} tone={intentTone} preview={!isExpanded} />
                  <StoryMetric label="Churn risk" value={scoreToRating(churn_risk)} bar={churn_risk} tone={riskTone} preview={!isExpanded} />
                  <StoryMetric label="Objection" value={main_objection || "—"} tone={objectionRiskTone} icon={Flag} preview={!isExpanded} multiline />
                </div>
              )}

              {isExpanded && (
                <>
                  {nodes.length > 0 && (
                    <div className={`relationship-story-flow is-${nodes.length}`} aria-label="Relationship flow">
                      {nodes.map((node, nodeIndex) => (
                        <div className={`relationship-story-flow__segment is-${node.tone}`} key={`${call_id}-${node.phase}`}>
                          <FlowNode {...node} connected={nodeIndex > 0} />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="relationship-story-card__actions">
                    {linkProps?.to && !linkProps.disabled && (
                      <Link
                        to={linkProps.to}
                        state={linkProps.state}
                        className="relationship-story-card__open"
                      >
                        Open call
                        <ArrowRight size={14} weight="bold" />
                      </Link>
                    )}
                    {linkProps?.disabled && (
                      <span className="relationship-story-card__open is-disabled">Historical preview</span>
                    )}
                  </div>
                </>
              )}
            </article>
          </div>
        );
      })}
    </div>
  );
}
